import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    Bot, Package, Settings, BarChart3, Users, Edit, Trash2,
    Plus, Save, DollarSign, TrendingUp, RefreshCw
} from 'lucide-react';

const AdminBotTradingPage = () => {
    const [activeTab, setActiveTab] = useState('packages');
    const [loading, setLoading] = useState(true);
    const [packages, setPackages] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [settings, setSettings] = useState({});
    const [editingPackage, setEditingPackage] = useState(null);
    const [showPackageForm, setShowPackageForm] = useState(false);

    const [packageForm, setPackageForm] = useState({
        name: '',
        price: '',
        durationDays: 30,
        minDailyProfitPercent: '0.3',
        maxDailyProfitPercent: '2.0',
        maxInvestment: '10000',
        description: '',
        isActive: true
    });

    const [settingsForm, setSettingsForm] = useState({
        BOT_TRADING_ENABLED: 'true',
        BOT_EXCHANGE_PROFIT_SHARE: '20',
        BOT_MIN_INVESTMENT: '10',
        BOT_MAX_INVESTMENT: '10000',
        BOT_TRADING_FEE: '0.02',
        BOT_TAKE_PROFIT_PERCENT: '1',
        BOT_DCA_DROP_PERCENT: '2',
        BOT_MAX_OPEN_TRADES: '10'
    });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [packagesRes, subsRes, settingsRes] = await Promise.all([
                api.get('/admin/bot-trading/packages'),
                api.get('/admin/bot-trading/subscriptions'),
                api.get('/admin/bot-trading/settings')
            ]);

            setPackages(packagesRes.data.packages || []);
            setSubscriptions(subsRes.data.subscriptions || []);
            setSettings(settingsRes.data.settings || {});
            setSettingsForm(prev => ({
                ...prev,
                ...settingsRes.data.settings
            }));
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSavePackage = async () => {
        try {
            if (editingPackage) {
                await api.put(`/admin/bot-trading/packages/${editingPackage.id}`, packageForm);
                toast.success('Package updated');
            } else {
                await api.post('/admin/bot-trading/packages', packageForm);
                toast.success('Package created');
            }
            setShowPackageForm(false);
            setEditingPackage(null);
            resetPackageForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save package');
        }
    };

    const handleDeletePackage = async (id) => {
        if (!window.confirm('Are you sure you want to delete this package?')) return;

        try {
            await api.delete(`/admin/bot-trading/packages/${id}`);
            toast.success('Package deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to delete package');
        }
    };

    const handleSaveSettings = async () => {
        try {
            await api.put('/admin/bot-trading/settings', settingsForm);
            toast.success('Settings saved');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save settings');
        }
    };

    const resetPackageForm = () => {
        setPackageForm({
            name: '',
            price: '',
            durationDays: 30,
            minDailyProfitPercent: '0.3',
            maxDailyProfitPercent: '2.0',
            maxInvestment: '10000',
            description: '',
            isActive: true
        });
    };

    const startEditPackage = (pkg) => {
        setEditingPackage(pkg);
        setPackageForm({
            name: pkg.name,
            price: pkg.price,
            durationDays: pkg.durationDays,
            minDailyProfitPercent: pkg.minDailyProfitPercent,
            maxDailyProfitPercent: pkg.maxDailyProfitPercent,
            maxInvestment: pkg.maxInvestment,
            description: pkg.description || '',
            isActive: pkg.isActive
        });
        setShowPackageForm(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900">
                <AdminNavbar />
                <div className="flex items-center justify-center py-20">
                    <RefreshCw className="animate-spin text-yellow-500" size={40} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            <div className="container mx-auto px-6 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Bot className="text-yellow-500" /> Bot Trading Management
                        </h1>
                        <p className="text-gray-400 text-sm mt-1">
                            Manage packages, settings, and monitor subscriptions
                        </p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
                    {[
                        { id: 'packages', label: 'Packages', icon: Package },
                        { id: 'settings', label: 'Settings', icon: Settings },
                        { id: 'subscriptions', label: 'Subscriptions', icon: Users },
                        { id: 'reports', label: 'Reports', icon: BarChart3 }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === tab.id
                                ? 'bg-yellow-500 text-black font-medium'
                                : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            <tab.icon size={18} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Packages Tab */}
                {activeTab === 'packages' && (
                    <div className="space-y-6">
                        <div className="flex justify-end">
                            <button
                                onClick={() => {
                                    resetPackageForm();
                                    setEditingPackage(null);
                                    setShowPackageForm(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400"
                            >
                                <Plus size={18} /> Add Package
                            </button>
                        </div>

                        {/* Package Form Modal */}
                        {showPackageForm && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg">
                                    <h3 className="text-lg font-semibold text-white mb-4">
                                        {editingPackage ? 'Edit Package' : 'Create Package'}
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-gray-400 text-sm mb-1">Name</label>
                                            <input
                                                type="text"
                                                value={packageForm.name}
                                                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-gray-400 text-sm mb-1">Price (USDT)</label>
                                                <input
                                                    type="number"
                                                    value={packageForm.price}
                                                    onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-gray-400 text-sm mb-1">Duration (Days)</label>
                                                <input
                                                    type="number"
                                                    value={packageForm.durationDays}
                                                    onChange={(e) => setPackageForm({ ...packageForm, durationDays: parseInt(e.target.value) })}
                                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-gray-400 text-sm mb-1">Min Daily Profit %</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={packageForm.minDailyProfitPercent}
                                                    onChange={(e) => setPackageForm({ ...packageForm, minDailyProfitPercent: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-gray-400 text-sm mb-1">Max Daily Profit %</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={packageForm.maxDailyProfitPercent}
                                                    onChange={(e) => setPackageForm({ ...packageForm, maxDailyProfitPercent: e.target.value })}
                                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-gray-400 text-sm mb-1">Max Investment (USDT)</label>
                                            <input
                                                type="number"
                                                value={packageForm.maxInvestment}
                                                onChange={(e) => setPackageForm({ ...packageForm, maxInvestment: e.target.value })}
                                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-gray-400 text-sm mb-1">Description</label>
                                            <textarea
                                                value={packageForm.description}
                                                onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                                rows={2}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={packageForm.isActive}
                                                onChange={(e) => setPackageForm({ ...packageForm, isActive: e.target.checked })}
                                                className="rounded"
                                            />
                                            <label className="text-gray-400 text-sm">Active</label>
                                        </div>
                                    </div>
                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={() => setShowPackageForm(false)}
                                            className="flex-1 px-4 py-2 border border-slate-600 text-gray-400 rounded-lg hover:bg-slate-700"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSavePackage}
                                            className="flex-1 px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Packages List */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {packages.map((pkg) => (
                                <div key={pkg.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-white">{pkg.name}</h3>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => startEditPackage(pkg)}
                                                className="p-2 text-gray-400 hover:text-white"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeletePackage(pkg.id)}
                                                className="p-2 text-gray-400 hover:text-red-400"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold text-yellow-500 mb-2">
                                        ${parseFloat(pkg.price).toFixed(0)}/month
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Daily Profit</span>
                                            <span className="text-green-400">{pkg.minDailyProfitPercent}% - {pkg.maxDailyProfitPercent}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Max Investment</span>
                                            <span className="text-white">${parseFloat(pkg.maxInvestment).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Status</span>
                                            <span className={pkg.isActive ? 'text-green-400' : 'text-red-400'}>
                                                {pkg.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 max-w-2xl">
                        <h3 className="text-lg font-semibold text-white mb-6">Bot Trading Settings</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between py-3 border-b border-slate-700">
                                <div>
                                    <div className="text-white font-medium">Enable Bot Trading</div>
                                    <div className="text-gray-400 text-sm">Allow users to access bot trading</div>
                                </div>
                                <select
                                    value={settingsForm.BOT_TRADING_ENABLED}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, BOT_TRADING_ENABLED: e.target.value })}
                                    className="px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                                >
                                    <option value="true">Enabled</option>
                                    <option value="false">Disabled</option>
                                </select>
                            </div>

                            <div className="flex items-center justify-between py-3 border-b border-slate-700">
                                <div>
                                    <div className="text-white font-medium">Exchange Profit Share (%)</div>
                                    <div className="text-gray-400 text-sm">Percentage of profit exchange keeps</div>
                                </div>
                                <input
                                    type="number"
                                    value={settingsForm.BOT_EXCHANGE_PROFIT_SHARE}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, BOT_EXCHANGE_PROFIT_SHARE: e.target.value })}
                                    className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-right"
                                />
                            </div>

                            <div className="flex items-center justify-between py-3 border-b border-slate-700">
                                <div>
                                    <div className="text-white font-medium">Trading Fee (%)</div>
                                    <div className="text-gray-400 text-sm">Fee per trade (buy/sell)</div>
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={settingsForm.BOT_TRADING_FEE}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, BOT_TRADING_FEE: e.target.value })}
                                    className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-right"
                                />
                            </div>

                            <div className="flex items-center justify-between py-3 border-b border-slate-700">
                                <div>
                                    <div className="text-white font-medium">Min Investment (USDT)</div>
                                    <div className="text-gray-400 text-sm">Minimum investment amount</div>
                                </div>
                                <input
                                    type="number"
                                    value={settingsForm.BOT_MIN_INVESTMENT}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, BOT_MIN_INVESTMENT: e.target.value })}
                                    className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-right"
                                />
                            </div>

                            <div className="flex items-center justify-between py-3 border-b border-slate-700">
                                <div>
                                    <div className="text-white font-medium">Take Profit (%)</div>
                                    <div className="text-gray-400 text-sm">Close trade when profit reaches</div>
                                </div>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={settingsForm.BOT_TAKE_PROFIT_PERCENT}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, BOT_TAKE_PROFIT_PERCENT: e.target.value })}
                                    className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-right"
                                />
                            </div>

                            <div className="flex items-center justify-between py-3 border-b border-slate-700">
                                <div>
                                    <div className="text-white font-medium">DCA Drop (%)</div>
                                    <div className="text-gray-400 text-sm">Average down when price drops by</div>
                                </div>
                                <input
                                    type="number"
                                    step="0.5"
                                    value={settingsForm.BOT_DCA_DROP_PERCENT}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, BOT_DCA_DROP_PERCENT: e.target.value })}
                                    className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-right"
                                />
                            </div>

                            <div className="flex items-center justify-between py-3">
                                <div>
                                    <div className="text-white font-medium">Max Open Trades</div>
                                    <div className="text-gray-400 text-sm">Maximum concurrent trades per user</div>
                                </div>
                                <input
                                    type="number"
                                    value={settingsForm.BOT_MAX_OPEN_TRADES}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, BOT_MAX_OPEN_TRADES: e.target.value })}
                                    className="w-24 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-right"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleSaveSettings}
                            className="mt-6 flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400"
                        >
                            <Save size={18} /> Save Settings
                        </button>
                    </div>
                )}

                {/* Subscriptions Tab */}
                {activeTab === 'subscriptions' && (
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Active Subscriptions</h3>
                        {subscriptions.length === 0 ? (
                            <p className="text-gray-400 text-center py-8">No subscriptions yet</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="text-gray-400 text-sm border-b border-slate-700">
                                            <th className="text-left pb-3">User</th>
                                            <th className="text-left pb-3">Package</th>
                                            <th className="text-right pb-3">Investment</th>
                                            <th className="text-right pb-3">Profit</th>
                                            <th className="text-right pb-3">Trades</th>
                                            <th className="text-center pb-3">Status</th>
                                            <th className="text-left pb-3">Expires</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subscriptions.map((sub) => (
                                            <tr key={sub.id} className="border-b border-slate-700/50">
                                                <td className="py-3 text-white">{sub.user?.email || 'N/A'}</td>
                                                <td className="py-3 text-gray-400">{sub.package?.name || 'N/A'}</td>
                                                <td className="py-3 text-right text-white">
                                                    ${parseFloat(sub.investmentAmount).toFixed(2)}
                                                </td>
                                                <td className="py-3 text-right text-green-400">
                                                    +${parseFloat(sub.totalProfit).toFixed(4)}
                                                </td>
                                                <td className="py-3 text-right text-white">
                                                    {sub.totalTrades} ({sub.winningTrades} wins)
                                                </td>
                                                <td className="py-3 text-center">
                                                    <span className={`px-2 py-1 rounded text-xs ${sub.status === 'active'
                                                        ? 'bg-green-500/20 text-green-400'
                                                        : sub.status === 'expired'
                                                            ? 'bg-red-500/20 text-red-400'
                                                            : 'bg-yellow-500/20 text-yellow-400'
                                                        }`}>
                                                        {sub.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 text-gray-400">
                                                    {new Date(sub.endDate).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Reports Tab */}
                {activeTab === 'reports' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                    <Users size={16} /> Active Subscriptions
                                </div>
                                <div className="text-2xl font-bold text-white">
                                    {subscriptions.filter(s => s.status === 'active').length}
                                </div>
                            </div>
                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                    <DollarSign size={16} /> Total Investment
                                </div>
                                <div className="text-2xl font-bold text-white">
                                    ${subscriptions.reduce((sum, s) => sum + parseFloat(s.investmentAmount || 0), 0).toFixed(2)}
                                </div>
                            </div>
                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                    <TrendingUp size={16} /> Total Profit Distributed
                                </div>
                                <div className="text-2xl font-bold text-green-400">
                                    ${subscriptions.reduce((sum, s) => sum + parseFloat(s.totalProfit || 0), 0).toFixed(4)}
                                </div>
                            </div>
                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                                    <BarChart3 size={16} /> Exchange Earnings
                                </div>
                                <div className="text-2xl font-bold text-yellow-500">
                                    ${(subscriptions.reduce((sum, s) => sum + parseFloat(s.totalProfit || 0), 0) * (parseFloat(settings.BOT_EXCHANGE_PROFIT_SHARE || 20) / 100)).toFixed(4)}
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">Performance Overview</h3>
                            <p className="text-gray-400 text-center py-8">
                                Detailed reports and charts will be available once trading data is generated.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminBotTradingPage;
